import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import BackButton from '../components/BackButton';
import { Crown, Lock, Hammer, Sparkles, Gamepad2, Flame, ChevronRight } from 'lucide-react';
import { useDemo, useAppMode } from '../context/DemoContext';
import vaultCathedralBg from '../assets/leaderboard/vault_cathedral.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══ ROYAL PALETTE — single cohesive color story ═══
const GOLD = '#facc15';     // pure gilded gold
const GOLD_DEEP = '#b8860b'; // antique gold
const CRIMSON = '#dc2626';   // royal crimson accent
const BLOOD = '#7c1d1d';     // deep blood shadow

const GAME_TABS = [
  { id: 'overall',         name: 'All Vaults',    icon: Crown,     lore: 'Hall of Thieves' },
  { id: 'safe_cracker',    name: 'Safe Cracker',  icon: Lock,      lore: 'Breakers of the Locks' },
  { id: 'whack_a_gremlin', name: 'Snatch-a-Bag',  icon: Hammer,    lore: 'Purse Snatchers' },
  { id: 'slot_machine',    name: 'Fee Scrambler', icon: Sparkles,  lore: 'Tumbler Saints' },
];

// Lore-themed rank titles (top 10 get a title; rest get the order)
const RANK_TITLES = [
  'The Kingpin',
  'The Vault Lord',
  'The Fence',
  'The Shadow',
  'The Pickpocket',
  'The Smoke',
  'The Whisper',
  'The Courier',
  'The Gremlin',
  'The Initiate',
];

// Podium medal gradient lookups
const MEDALS = {
  1: { ring: '#fde047', glow: '#facc15', label: 'I' },   // gold
  2: { ring: '#d1d5db', glow: '#9ca3af', label: 'II' },  // silver
  3: { ring: '#d97706', glow: '#b45309', label: 'III' }, // bronze
};

// ═══ Decorative ornate frame used everywhere ═══
const OrnateFrame = ({ children, className = '', glow = true }) => (
  <div className={`relative ${className}`}>
    {/* Gilded corners */}
    <div className="absolute top-0 left-0 w-5 h-5 pointer-events-none">
      <div className="absolute top-0 left-0 w-5 h-[1.5px]" style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
      <div className="absolute top-0 left-0 w-[1.5px] h-5" style={{ background: `linear-gradient(180deg, ${GOLD}, transparent)` }} />
    </div>
    <div className="absolute top-0 right-0 w-5 h-5 pointer-events-none">
      <div className="absolute top-0 right-0 w-5 h-[1.5px]" style={{ background: `linear-gradient(270deg, ${GOLD}, transparent)` }} />
      <div className="absolute top-0 right-0 w-[1.5px] h-5" style={{ background: `linear-gradient(180deg, ${GOLD}, transparent)` }} />
    </div>
    <div className="absolute bottom-0 left-0 w-5 h-5 pointer-events-none">
      <div className="absolute bottom-0 left-0 w-5 h-[1.5px]" style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
      <div className="absolute bottom-0 left-0 w-[1.5px] h-5" style={{ background: `linear-gradient(0deg, ${GOLD}, transparent)` }} />
    </div>
    <div className="absolute bottom-0 right-0 w-5 h-5 pointer-events-none">
      <div className="absolute bottom-0 right-0 w-5 h-[1.5px]" style={{ background: `linear-gradient(270deg, ${GOLD}, transparent)` }} />
      <div className="absolute bottom-0 right-0 w-[1.5px] h-5" style={{ background: `linear-gradient(0deg, ${GOLD}, transparent)` }} />
    </div>
    {/* Body */}
    <div
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(22,10,8,0.92) 0%, rgba(12,5,4,0.95) 100%)',
        border: `1px solid ${GOLD}28`,
        boxShadow: glow
          ? `inset 0 0 40px rgba(220,38,38,0.08), 0 0 30px rgba(250,204,21,0.08)`
          : 'inset 0 0 30px rgba(220,38,38,0.05)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {children}
    </div>
  </div>
);

// Lightweight tier mapping for on-chain SP totals (mirrors backend ARCADE_TIERS).
const ON_CHAIN_TIERS = [
  { tier: 'rookie',   min: 0,      color: '#6b7280', title: 'Sewer Rat' },
  { tier: 'bronze',   min: 100,    color: '#cd7f32', title: 'Drain Lurker' },
  { tier: 'silver',   min: 500,    color: '#c0c0c0', title: 'Pipe Runner' },
  { tier: 'gold',     min: 1500,   color: '#ffd700', title: 'Tunnel Phantom' },
  { tier: 'platinum', min: 3500,   color: '#00ffff', title: 'Grime Warden' },
  { tier: 'diamond',  min: 7500,   color: '#00bfff', title: 'Void Stalker' },
  { tier: 'master',   min: 15000,  color: '#a855f7', title: 'Gas Baron' },
];
const getOnchainTier = (sp) => {
  let t = ON_CHAIN_TIERS[0];
  for (const x of ON_CHAIN_TIERS) if (sp >= x.min) t = x;
  return t;
};

export default function ArcadeLeaderboardPage() {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toBase58();
  const { isDemo } = useDemo();
  const { isLive } = useAppMode();

  const [activeTab, setActiveTab] = useState('overall');
  const [leaderboards, setLeaderboards] = useState({});
  const [loading, setLoading] = useState(true);
  const [onchainStats, setOnchainStats] = useState({ total_submissions: 0, unique_wallets: 0, season_number: 1 });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      // ─── Live mode (devnet) — fetch real on-chain `submit_sp` ledger ───
      if (isLive) {
        try {
          const r = await fetch(`${API_URL}/api/arcade/leaderboard/onchain/all?limit=25`);
          const data = await r.json();
          const overall = (data.leaderboard || []).map((e) => ({
            wallet_address: e.wallet_address,
            total_fees: e.total_sp,           // map SP → fees field for shared UI
            best_score: e.total_sp,
            games_played: e.submissions,
            tier: 'rookie',
            tier_info: getOnchainTier(e.total_sp),
            tx_signature: e.last_signature,
            explorer_url: e.last_explorer_url,
            last_at: e.last_at,
          }));
          // Per-game breakdown derived from per_game subtotals
          const perGame = { safe_cracker: [], whack_a_gremlin: [], slot_machine: [] };
          (data.leaderboard || []).forEach((e) => {
            ['safe_cracker', 'whack_a_gremlin', 'slot_machine'].forEach((g) => {
              const sp = (e.per_game || {})[g] || 0;
              if (sp > 0) {
                perGame[g].push({
                  wallet_address: e.wallet_address,
                  total_fees: sp,
                  best_score: sp,
                  games_played: e.submissions,
                  tier: 'rookie',
                  tier_info: getOnchainTier(sp),
                  tx_signature: e.last_signature,
                  explorer_url: e.last_explorer_url,
                });
              }
            });
          });
          // Re-rank per game
          Object.keys(perGame).forEach((g) => {
            perGame[g] = perGame[g].sort((a, b) => b.total_fees - a.total_fees).map((row, i) => ({ ...row, rank: i + 1 }));
          });
          setLeaderboards({ overall, ...perGame });
          setOnchainStats({
            total_submissions: data.total_submissions || 0,
            unique_wallets: data.unique_wallets || 0,
            season_number: data.season_number || 1,
          });
        } catch {
          setLeaderboards({ overall: [], safe_cracker: [], whack_a_gremlin: [], slot_machine: [] });
        } finally {
          setLoading(false);
        }
        return;
      }
      try {
        const endpoints = [
          { key: 'overall', url: `${API_URL}/api/arcade/leaderboard` },
          { key: 'safe_cracker', url: `${API_URL}/api/arcade/leaderboard/safe_cracker` },
          { key: 'whack_a_gremlin', url: `${API_URL}/api/arcade/leaderboard/whack_a_gremlin` },
          { key: 'slot_machine', url: `${API_URL}/api/arcade/leaderboard/slot_machine` },
        ];
        const results = await Promise.all(endpoints.map(e => fetch(e.url).then(r => r.json()).catch(() => ({}))));
        const lbs = {};
        endpoints.forEach((e, i) => { lbs[e.key] = results[i]?.leaderboard || []; });
        setLeaderboards(lbs);
      } catch { /* empty */ } finally { setLoading(false); }
    };
    fetchAll();
  }, [isDemo, isLive]);

  const currentTab = GAME_TABS.find(t => t.id === activeTab) || GAME_TABS[0];
  const currentData = activeTab === 'overall'
    ? (leaderboards.overall || []).map(e => ({ ...e, best_score: e.total_fees }))
    : (leaderboards[activeTab] || []);
  const top3 = currentData.slice(0, 3);
  const rest = currentData.slice(3);

  return (
    <div className="min-h-screen text-white relative overflow-hidden" data-testid="leaderboard-page" style={{ background: '#0a0405' }}>
      {/* ═══ Vault Cathedral backdrop — fixed, masked to fade at edges & around content ═══ */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `url(${vaultCathedralBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          opacity: 0.45,
          maskImage: 'radial-gradient(ellipse 90% 75% at 50% 38%, black 0%, black 25%, rgba(0,0,0,0.55) 60%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 75% at 50% 38%, black 0%, black 25%, rgba(0,0,0,0.55) 60%, transparent 100%)',
        }}
      />
      {/* Dark vignette wash over the cathedral to keep UI readable */}
      <div className="fixed inset-0 z-[1] pointer-events-none" style={{
        background: 'linear-gradient(180deg, rgba(10,4,5,0.35) 0%, rgba(10,4,5,0.55) 55%, rgba(10,4,5,0.85) 100%)',
      }} />

      {/* ═══ Deep royal ambient ═══ */}
      <div className="fixed inset-0 z-[2]" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 15%, rgba(42,13,13,0.55) 0%, transparent 55%)',
      }} />

      {/* Gold dust sparkle — subtle floating particles */}
      <div className="fixed inset-0 z-[1] pointer-events-none overflow-hidden">
        {[...Array(14)].map((_, i) => (
          <motion.div key={i} className="absolute rounded-full"
            style={{
              left: `${(i * 7.3) % 100}%`,
              bottom: '-6px',
              width: i % 3 === 0 ? '2px' : '1px',
              height: i % 3 === 0 ? '2px' : '1px',
              background: GOLD,
              boxShadow: `0 0 6px ${GOLD}, 0 0 12px ${GOLD}60`,
            }}
            animate={{ y: [0, -1000], opacity: [0, 0.8, 0.6, 0] }}
            transition={{ duration: 12 + (i % 5) * 2, delay: i * 0.8, repeat: Infinity, ease: 'linear' }}
          />
        ))}
      </div>

      {/* Scanline / royal film grain overlay */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay opacity-[0.07]" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(250,204,21,0.15) 2px, rgba(250,204,21,0.15) 3px)',
      }} />

      {/* Top crimson wash — cathedral light */}
      <motion.div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] pointer-events-none z-[1]"
        style={{
          background: `radial-gradient(ellipse at 50% 0%, ${CRIMSON}22 0%, transparent 65%)`,
          filter: 'blur(40px)',
        }}
        animate={{ opacity: [0.6, 0.9, 0.6] }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      <BackButton to="/arcade" label="Arcade" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 pt-20 pb-16">

        {/* ═══ Cathedral Header ═══ */}
        <div className="text-center mb-10 md:mb-14">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-3"
          >
            <div className="h-[1px] w-12 md:w-20" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}70)` }} />
            <Crown className="w-4 h-4 md:w-5 md:h-5" style={{ color: GOLD, filter: `drop-shadow(0 0 10px ${GOLD})` }} />
            <div className="h-[1px] w-12 md:w-20" style={{ background: `linear-gradient(270deg, transparent, ${GOLD}70)` }} />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-[10px] md:text-xs uppercase tracking-[0.6em] mb-3 font-bold"
            style={{ fontFamily: "'Cinzel', serif", color: `${GOLD}` }}
            data-testid="leaderboard-overline"
          >
            {currentTab.lore}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase leading-none"
            style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              background: `linear-gradient(180deg, #fef08a 0%, ${GOLD} 45%, #fbbf24 70%, ${GOLD_DEEP} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: `drop-shadow(0 4px 30px ${GOLD}55) drop-shadow(0 0 40px ${CRIMSON}35)`,
              letterSpacing: '0.03em',
            }}
            data-testid="leaderboard-title"
          >
            Leaderboard
          </motion.h1>

          <motion.div
            initial={{ width: 0 }} animate={{ width: '240px' }} transition={{ duration: 1, delay: 0.4 }}
            className="mx-auto mt-5 h-[1.5px]"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${CRIMSON}60 20%, ${GOLD} 50%, ${CRIMSON}60 80%, transparent 100%)`,
              boxShadow: `0 0 10px ${GOLD}60`,
            }}
          />

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
            className="mt-4 text-[10px] md:text-xs uppercase tracking-[0.35em]"
            style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.35)' }}
          >
            ❝ the names etched in gold shall echo beyond the sewers ❞
          </motion.p>
        </div>

        {/* ═══ Game Tabs — all one color now ═══ */}
        <OrnateFrame className="mb-6 md:mb-8" glow={false}>
          <div className="p-2 md:p-3">
            <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {GAME_TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className="group relative flex items-center gap-2 px-3 md:px-5 py-2 md:py-2.5 text-[10px] md:text-xs font-bold tracking-[0.15em] uppercase transition-all flex-shrink-0"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      background: isActive
                        ? `linear-gradient(180deg, ${GOLD}20, ${BLOOD}25)`
                        : 'transparent',
                      border: `1px solid ${isActive ? `${GOLD}60` : 'transparent'}`,
                      color: isActive ? GOLD : 'rgba(250,204,21,0.35)',
                      boxShadow: isActive
                        ? `0 0 18px ${GOLD}25, inset 0 0 12px ${CRIMSON}15`
                        : 'none',
                      textShadow: isActive ? `0 0 8px ${GOLD}80` : 'none',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    {tab.name}
                  </button>
                );
              })}
            </div>
          </div>
        </OrnateFrame>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
          >
            {loading ? (
              <div className="py-24 text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                  className="w-10 h-10 border-2 rounded-full mx-auto mb-4"
                  style={{ borderColor: `${GOLD}20`, borderTopColor: GOLD }}
                />
                <p className="text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: `${GOLD}55` }}>
                  Conjuring the ledger...
                </p>
              </div>
            ) : currentData.length === 0 ? (
              isLive ? (
                /* Live-mode empty state — minimal terminal-style, dev-friendly */
                <div className="py-12 md:py-16" data-testid="leaderboard-live-empty">
                  <div
                    className="max-w-xl mx-auto px-6 py-6"
                    style={{
                      background: 'linear-gradient(180deg, rgba(8,18,28,0.85) 0%, rgba(4,10,18,0.92) 100%)',
                      border: '1px solid rgba(125,208,255,0.32)',
                      borderLeft: '3px solid #7dd0ff',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="inline-block rounded-full"
                        style={{
                          width: 6, height: 6,
                          background: '#7dd0ff',
                          boxShadow: '0 0 6px #7dd0ff',
                        }}
                      />
                      <span
                        className="text-[10px] tracking-[0.32em] font-bold uppercase"
                        style={{ color: '#a8def7', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                        data-testid="leaderboard-live-stats"
                      >
                        DEVNET · S{onchainStats.season_number} · {onchainStats.total_submissions} TX · {onchainStats.unique_wallets} SOULS
                      </span>
                    </div>
                    <p
                      className="text-[14px] leading-relaxed mb-3"
                      style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(220,235,250,0.85)' }}
                    >
                      Arcade scores write on chain via{' '}
                      <code
                        className="px-1.5 py-0.5 text-[12px]"
                        style={{
                          color: '#7dd0ff',
                          background: 'rgba(125,208,255,0.08)',
                          border: '1px solid rgba(125,208,255,0.25)',
                          fontFamily: "'JetBrains Mono','Courier New',monospace",
                        }}
                      >submit_sp</code>{' '}
                      after every game. The first soul to play in Live Mode lights this board up.
                    </p>
                    <div className="flex flex-wrap gap-2 items-center">
                      <a
                        href={`https://explorer.solana.com/address/${'C5vRjZZAjG1uzcir9y2tw6dwMjH79fPwQgoKaSjEnACT'}?cluster=devnet`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="leaderboard-live-program-link"
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] tracking-[0.18em] hover:brightness-125 transition"
                        style={{
                          fontFamily: "'JetBrains Mono','Courier New',monospace",
                          color: '#7dd0ff',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(125,208,255,0.35)',
                        }}
                      >view program ↗</a>
                      <button
                        onClick={() => navigate('/arcade')}
                        data-testid="leaderboard-live-arcade-btn"
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] tracking-[0.18em] hover:brightness-125 transition active:scale-[0.97]"
                        style={{
                          fontFamily: "'JetBrains Mono','Courier New',monospace",
                          color: '#a8def7',
                          background: 'rgba(125,208,255,0.06)',
                          border: '1px solid rgba(125,208,255,0.45)',
                        }}
                      >play arcade ↗</button>
                    </div>
                  </div>
                </div>
              ) : (
                <OrnateFrame className="py-20 text-center">
                  <div className="relative z-10">
                    <motion.div
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <Gamepad2 className="w-14 h-14 mx-auto mb-5" style={{ color: `${GOLD}45`, filter: `drop-shadow(0 0 12px ${GOLD}30)` }} />
                    </motion.div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-2 uppercase tracking-wider" style={{
                      fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                      background: `linear-gradient(180deg, ${GOLD}, ${GOLD_DEEP})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      The Vaults Sleep
                    </h3>
                    <p className="text-xs md:text-sm italic mb-8 max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: `${GOLD}40` }}>
                      No thief has yet cracked the seal.<br className="hidden md:block" />
                      Be the first to carve your name in gold.
                    </p>
                    <motion.button
                      onClick={() => navigate('/arcade')}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="inline-flex items-center gap-2 px-7 py-3 text-xs font-bold tracking-[0.25em] uppercase"
                      style={{
                        fontFamily: "'Cinzel', serif",
                        background: `linear-gradient(180deg, ${GOLD}20, ${BLOOD}40)`,
                        border: `1px solid ${GOLD}70`,
                        color: GOLD,
                        boxShadow: `0 0 20px ${GOLD}30, inset 0 0 15px ${CRIMSON}15`,
                        textShadow: `0 0 8px ${GOLD}80`,
                      }}
                      data-testid="play-now-btn"
                    >
                      <Flame className="w-4 h-4" /> Enter the Vaults
                    </motion.button>
                  </div>
                </OrnateFrame>
              )
            ) : (
              <>
                {/* ═══ PODIUM — cinematic AAA style ═══ */}
                {top3.length >= 3 && (
                  <OrnateFrame className="mb-6" glow>
                    <div className="p-6 md:p-10 relative">
                      {/* Inner radial spotlight */}
                      <div className="absolute inset-0 pointer-events-none" style={{
                        background: `radial-gradient(ellipse 60% 70% at 50% 20%, ${GOLD}12 0%, transparent 65%)`,
                      }} />

                      <div className="relative z-10 grid grid-cols-3 gap-3 md:gap-8 items-end">
                        {[top3[1], top3[0], top3[2]].map((entry, idx) => {
                          const rank = [2, 1, 3][idx];
                          const m = MEDALS[rank];
                          const heights = { 1: 'h-32 md:h-44', 2: 'h-20 md:h-32', 3: 'h-14 md:h-24' };
                          const title = RANK_TITLES[rank - 1];
                          return (
                            <motion.div
                              key={rank}
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.15 + idx * 0.15, duration: 0.6 }}
                              className="flex flex-col items-center text-center"
                            >
                              {/* Floating crown on #1 */}
                              {rank === 1 && (
                                <motion.div
                                  animate={{ y: [0, -5, 0], rotate: [-2, 2, -2] }}
                                  transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                                  className="mb-1"
                                >
                                  <Crown
                                    className="w-7 h-7 md:w-9 md:h-9"
                                    style={{
                                      color: GOLD,
                                      filter: `drop-shadow(0 0 12px ${GOLD}) drop-shadow(0 0 24px ${GOLD}80)`,
                                    }}
                                  />
                                </motion.div>
                              )}

                              {/* Medallion */}
                              <motion.div
                                className="relative w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-3"
                                style={{
                                  background: `radial-gradient(circle at 35% 25%, ${m.ring}60 0%, ${m.glow}30 50%, ${m.glow}10 100%)`,
                                  border: `2px solid ${m.ring}`,
                                  boxShadow: `0 0 25px ${m.glow}60, inset 0 0 12px ${m.glow}30`,
                                }}
                                whileHover={{ scale: 1.08 }}
                              >
                                <span className="text-base md:text-lg font-black" style={{
                                  fontFamily: "'Cinzel Decorative', serif",
                                  color: '#0f0a00',
                                  textShadow: `0 0 4px ${m.ring}`,
                                }}>
                                  {m.label}
                                </span>
                              </motion.div>

                              {/* Name */}
                              <p
                                className="text-xs md:text-sm font-bold uppercase tracking-wider truncate max-w-[90px] md:max-w-[140px] mb-0.5"
                                style={{ fontFamily: "'Cinzel', serif", color: GOLD }}
                              >
                                {entry.username || `${entry.wallet_address?.slice(0, 4)}...`}
                              </p>

                              {/* Title */}
                              <p
                                className="text-[8px] md:text-[10px] italic tracking-[0.15em] uppercase mb-2"
                                style={{ fontFamily: "'Courier New', monospace", color: `${GOLD}50` }}
                              >
                                {title}
                              </p>

                              {/* Score */}
                              <p
                                className="text-xl md:text-2xl font-black mb-3"
                                style={{
                                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                                  background: `linear-gradient(180deg, #fef08a, ${m.ring})`,
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  filter: `drop-shadow(0 0 8px ${m.glow}70)`,
                                  letterSpacing: '0.03em',
                                }}
                              >
                                {(entry.best_score || entry.total_fees || 0).toLocaleString()}
                              </p>

                              {/* Pedestal */}
                              <motion.div
                                className={`w-full ${heights[rank]} relative overflow-hidden`}
                                initial={{ scaleY: 0 }}
                                animate={{ scaleY: 1 }}
                                transition={{ duration: 0.8, delay: 0.5 + idx * 0.15, ease: 'easeOut' }}
                                style={{
                                  transformOrigin: 'bottom',
                                  background: `linear-gradient(180deg, ${m.glow}18 0%, ${BLOOD}30 100%)`,
                                  border: `1px solid ${m.ring}40`,
                                  borderBottom: 'none',
                                  boxShadow: `inset 0 0 20px ${m.glow}15`,
                                }}
                              >
                                {/* Top rim shine */}
                                <div className="absolute top-0 left-0 right-0 h-[2px]" style={{
                                  background: `linear-gradient(90deg, transparent, ${m.ring}, transparent)`,
                                  boxShadow: `0 0 8px ${m.ring}`,
                                }} />
                                {/* Roman numeral watermark */}
                                <div
                                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                  style={{
                                    fontFamily: "'Cinzel Decorative', serif",
                                    fontSize: rank === 1 ? '4rem' : '3rem',
                                    color: `${m.ring}`,
                                    opacity: 0.08,
                                    fontWeight: 700,
                                  }}
                                >
                                  {m.label}
                                </div>
                              </motion.div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </OrnateFrame>
                )}

                {/* ═══ Full Ranking List ═══ */}
                <OrnateFrame glow={false}>
                  <div className="p-4 md:p-6">
                    {/* Section header */}
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b" style={{ borderColor: `${GOLD}20` }}>
                      <span className="text-[10px] md:text-xs tracking-[0.35em] uppercase font-bold" style={{
                        fontFamily: "'Cinzel', serif", color: GOLD,
                      }}>
                        The Ledger
                      </span>
                      <div className="flex-1 h-[1px]" style={{ background: `linear-gradient(90deg, ${GOLD}40, transparent)` }} />
                      <span className="text-[9px] tracking-[0.2em] uppercase" style={{
                        fontFamily: "'Courier New', monospace", color: `${GOLD}40`,
                      }}>
                        {currentData.length} {currentData.length === 1 ? 'thief' : 'thieves'}
                      </span>
                    </div>

                    <div className="space-y-1.5 relative z-10">
                      {(top3.length < 3 ? currentData : rest).map((entry, i) => {
                        const rank = top3.length < 3 ? i + 1 : i + 4;
                        const isYou = entry.wallet_address === walletAddress;
                        const title = rank <= 10 ? RANK_TITLES[rank - 1] : null;
                        const isTopTen = rank <= 10;

                        return (
                          <motion.div
                            key={entry.wallet_address || i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.03 + i * 0.025 }}
                            className="group relative flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 transition-all"
                            style={{
                              background: isYou
                                ? `linear-gradient(90deg, ${GOLD}15, ${CRIMSON}10)`
                                : 'rgba(250,204,21,0.02)',
                              border: `1px solid ${isYou ? `${GOLD}60` : `${GOLD}12`}`,
                              boxShadow: isYou ? `0 0 12px ${GOLD}20, inset 0 0 10px ${CRIMSON}10` : 'none',
                            }}
                            data-testid={`leaderboard-row-${rank}`}
                          >
                            {/* Rank number */}
                            <span
                              className="w-9 md:w-10 text-center text-sm md:text-base font-black"
                              style={{
                                fontFamily: "'Cinzel Decorative', serif",
                                color: isTopTen ? GOLD : `${GOLD}45`,
                                textShadow: isTopTen ? `0 0 8px ${GOLD}50` : 'none',
                              }}
                            >
                              {rank}
                            </span>

                            {/* Gold divider dot */}
                            <div className="w-[2px] h-6 md:h-8" style={{
                              background: `linear-gradient(180deg, transparent, ${GOLD}50, transparent)`,
                            }} />

                            {/* Name + title */}
                            <div className="flex-1 min-w-0">
                              <span
                                className="text-xs md:text-sm font-bold truncate block tracking-wide"
                                style={{
                                  fontFamily: "'Cinzel', serif",
                                  color: isYou ? GOLD : 'rgba(254,240,138,0.85)',
                                  textShadow: isYou ? `0 0 6px ${GOLD}40` : 'none',
                                }}
                              >
                                {entry.username || `${entry.wallet_address?.slice(0, 6)}...${entry.wallet_address?.slice(-4)}`}
                              </span>
                              {title && (
                                <span
                                  className="text-[8px] md:text-[9px] italic tracking-[0.15em] uppercase"
                                  style={{ fontFamily: "'Courier New', monospace", color: `${GOLD}40` }}
                                >
                                  {title}
                                </span>
                              )}
                            </div>

                            {isYou && (
                              <span
                                className="text-[8px] md:text-[9px] px-2 py-0.5 font-black tracking-[0.2em]"
                                style={{
                                  fontFamily: "'Cinzel', serif",
                                  background: `linear-gradient(180deg, ${GOLD}, ${GOLD_DEEP})`,
                                  color: '#0f0500',
                                  boxShadow: `0 0 8px ${GOLD}60`,
                                }}
                              >
                                YOU
                              </span>
                            )}

                            {/* Score */}
                            <span
                              className="text-sm md:text-base font-black flex-shrink-0 tabular-nums"
                              style={{
                                fontFamily: "'Impact', 'Arial Black', sans-serif",
                                background: `linear-gradient(180deg, #fef08a, ${isTopTen ? GOLD : `${GOLD}80`})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                filter: isTopTen ? `drop-shadow(0 0 4px ${GOLD}50)` : 'none',
                                letterSpacing: '0.02em',
                              }}
                            >
                              {(entry.best_score || entry.total_fees || 0).toLocaleString()}
                            </span>

                            <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition-opacity" style={{ color: GOLD }} />
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </OrnateFrame>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Foot flourish */}
        <div className="flex items-center justify-center gap-3 mt-10 md:mt-14">
          <div className="h-[1px] w-20 md:w-32" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}50)` }} />
          <Flame className="w-3 h-3" style={{ color: `${GOLD}70` }} />
          <p className="text-[9px] md:text-[10px] tracking-[0.4em] uppercase" style={{
            fontFamily: "'Courier New', monospace", color: `${GOLD}40`,
          }}>
            Rankings update live
          </p>
          <Flame className="w-3 h-3" style={{ color: `${GOLD}70` }} />
          <div className="h-[1px] w-20 md:w-32" style={{ background: `linear-gradient(270deg, transparent, ${GOLD}50)` }} />
        </div>
      </div>
    </div>
  );
}
